/**
 * ui.js — small shared presentational pieces used across pages.
 *
 * StatusBadge  — text + symbol, never colour alone. `tone` picks the tint;
 *                the label is always the caller's real domain status.
 * StatePanel   — empty / error / no-access panel. Use only after the request
 *                has settled (empty = successful zero-data response).
 * SkeletonRows — loading placeholder rows.
 * PageHead     — page title block + actions.
 * Spinner      — inline activity indicator for pending buttons.
 * useMediaQuery — render-time breakpoint (for table ↔ card-list swaps, where
 *                 CSS-only hiding would duplicate per-row state).
 * MenuButton   — "More actions" disclosure: Escape / outside click close it
 *                and return focus to the trigger.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import Icon from './Icon';

const TONE_ICON = {
  success: 'checkCircle',
  warning: 'clock',
  danger: 'alert',
  info: 'refresh',
  accent: 'review',
  neutral: null,
};

export function StatusBadge({ tone = 'neutral', icon, children, title }) {
  const name = icon === undefined ? TONE_ICON[tone] : icon;
  return (
    <span className={`status-badge status-${tone}`} title={title}>
      {name ? <Icon name={name} size={14} /> : <span className="dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

export function StatePanel({ kind = 'empty', icon, title, children, action, compact = false }) {
  const defaultIcon = kind === 'error' ? 'alert' : kind === 'noaccess' ? 'lock' : 'file';
  return (
    <div
      className={`state-panel ${kind === 'error' ? 'is-error' : ''}`}
      role={kind === 'error' ? 'alert' : undefined}
      style={compact ? { padding: '32px 24px' } : undefined}
    >
      <Icon name={icon || defaultIcon} size={28} />
      <div className="state-title">{title}</div>
      {children && <div className="state-body">{children}</div>}
      {action}
    </div>
  );
}

export function SkeletonRows({ rows = 3, height = 52, label = 'Loading' }) {
  return (
    <div role="status" aria-label={label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height }} />
      ))}
    </div>
  );
}

export function PageHead({ title, subtitle, breadcrumb, actions, titleId }) {
  return (
    <header className="page-head">
      <div className="page-head-text">
        {breadcrumb}
        <h1 className="page-title" id={titleId}>{title}</h1>
        {subtitle && <p className="page-sub">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Spinner({ size = 16 }) {
  return <span className="spinner" aria-hidden="true" style={{ width: size, height: size }} />;
}

export function useMediaQuery(query) {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export const NARROW = '(max-width: 767px)';

export function MenuButton({ label = 'More actions', children, align = 'right', icon = 'more' }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const id = useId();

  useEffect(() => {
    if (!open) return undefined;
    panelRef.current?.querySelector('button, select, a[href], input')?.focus();
    const onDown = (e) => {
      if (!panelRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); btnRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const close = () => { setOpen(false); btnRef.current?.focus(); };

  return (
    <div className="menu-wrap">
      <button
        ref={btnRef}
        type="button"
        className="icon-btn"
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(o => !o)}
      >
        <Icon name={icon} />
      </button>
      {open && (
        <div ref={panelRef} id={id} className={`menu-panel menu-${align}`}>
          {typeof children === 'function' ? children({ close }) : children}
        </div>
      )}
    </div>
  );
}
